import {
  ECommuteDistanceType,
  EHeatingSourceType,
  EHouseMaterialType,
  ELocationType,
  ENewobjectType,
  EOtherCommunications,
  EReadyStatus,
  ESaleStatus,
  ESorting,
  EVillageClass,
  EWcLocationType,
  IGetVillageListRequestSchema,
} from '../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import { MOSCOW_MO, SPB_LO } from '../components/cian/location_switcher/util';
import { EModerationStatus } from '../../node/repositories/countryside-search/entities/builder/BuilderForListPresenterSchema';
import { IVillageCounterSetSchema } from '../../node/repositories/countryside-search/entities/counter/VillageCounterSetSchema';
import { INewobjectCategorySetForListPresenterSchema } from '../../node/repositories/countryside-search/entities/newobject_category/NewobjectCategorySetForListPresenterSchema';

import { ICity } from '../store/geo';
import { getSortingKeys } from '../utils/sorting';

export type TVariant = [string, string, string];

export const OBJECT_VARIANTS: TVariant = ['объект', 'объекта', 'объектов'];
export const FOUND_VARIANTS: TVariant = ['Найден', 'Найдено', 'Найдено'];
export const OFFER_VARIANTS: TVariant = ['посёлок', 'посёлка', 'посёлков'];
export const MOBILE_OFFER_VARIANTS: TVariant = ['посёлок', 'посёлка', 'посёлков'];

export type TIndexableObject = {
  [key: string]: string;
};

export enum NEW_OBJECTS_KEYS {
  COTTAGE = 'cottage',
  TOWNHOUSE = 'townhouse',
  LOWRISEHOUSE = 'lowrisehouse',
  LANDPLOT = 'landplot',
}

export const NEW_OBJECTS_SET_KEYS: { [key: string]: keyof INewobjectCategorySetForListPresenterSchema } = {
  [NEW_OBJECTS_KEYS.COTTAGE]: 'cottage',
  [NEW_OBJECTS_KEYS.TOWNHOUSE]: 'townhouse',
  [NEW_OBJECTS_KEYS.LOWRISEHOUSE]: 'lowrisehouse',
  [NEW_OBJECTS_KEYS.LANDPLOT]: 'landplot',
};

export const NEW_OBJECTS_NAMES: TIndexableObject = {
  [NEW_OBJECTS_KEYS.COTTAGE]: 'Коттеджи',
  [NEW_OBJECTS_KEYS.TOWNHOUSE]: 'Таунхаусы',
  [NEW_OBJECTS_KEYS.LOWRISEHOUSE]: 'Малоэтажные дома',
  [NEW_OBJECTS_KEYS.LANDPLOT]: 'Участки',
};

export const NEW_OBJECTS_SHORT_NAMES: TIndexableObject = {
  [NEW_OBJECTS_KEYS.COTTAGE]: 'Коттеджи',
  [NEW_OBJECTS_KEYS.TOWNHOUSE]: 'Таунхаусы',
  [NEW_OBJECTS_KEYS.LOWRISEHOUSE]: 'Малоэтаж. дома',
  [NEW_OBJECTS_KEYS.LANDPLOT]: 'Участки',
};

export const COMMUTE_DISTANCE_TYPES: ECommuteDistanceType[] = [ECommuteDistanceType.Road, ECommuteDistanceType.Station];

export const HEATING_SOURCE_TYPES: EHeatingSourceType[] = [
  EHeatingSourceType.Without,
  EHeatingSourceType.Gas,
  EHeatingSourceType.Coal,
  EHeatingSourceType.Stove,
  EHeatingSourceType.Fireplace,
  EHeatingSourceType.Electric,
];

export const NEWOBJECT_TYPES: ENewobjectType[] = [
  ENewobjectType.Cottage,
  ENewobjectType.Townhouse,
  ENewobjectType.Lowrisehouse,
  ENewobjectType.Landplot,
];

export const HOUSE_MATERIAL_TYPES: EHouseMaterialType[] = [
  EHouseMaterialType.Block,
  EHouseMaterialType.Boards,
  EHouseMaterialType.Brick,
  EHouseMaterialType.Custom,
  EHouseMaterialType.Monolith,
  EHouseMaterialType.MonolithBrick,
  EHouseMaterialType.None,
  EHouseMaterialType.Old,
  EHouseMaterialType.Panel,
  EHouseMaterialType.Stalin,
  EHouseMaterialType.Wood,
  EHouseMaterialType.AerocreteBlock,
  EHouseMaterialType.FoamConcreteBlock,
  EHouseMaterialType.AerocreteBlock,
  EHouseMaterialType.FoamConcreteBlock,
];

export const OTHER_COMMUNICATION_TYPES: EOtherCommunications[] = [
  EOtherCommunications.Drainage,
  EOtherCommunications.Electricity,
  EOtherCommunications.Gas,
  EOtherCommunications.Water,
];

export const READY_STATUS_TYPES: EReadyStatus[] = [
  EReadyStatus.Building,
  EReadyStatus.Built,
  EReadyStatus.Frozen,
  EReadyStatus.Planning,
];

export const SALE_STATUS_TYPES: ESaleStatus[] = [ESaleStatus.Closed, ESaleStatus.Opened, ESaleStatus.Pending];

export const VILLAGE_CLASS_TYPES: EVillageClass[] = [
  EVillageClass.Economy,
  EVillageClass.Comfort,
  EVillageClass.Business,
  EVillageClass.Elite,
];

export const WC_LOCATION_TYPES: EWcLocationType[] = [EWcLocationType.Indoors, EWcLocationType.Outdoors];

export const LOCATION_TYPES: ELocationType[] = [
  ELocationType.House,
  ELocationType.Location,
  ELocationType.Street,
  ELocationType.District,
];

export const SHORTEN_NUMBER_OPTIONS = {
  billionPostfix: 'млрд',
  fractionalLength: 1,
  millionPostfix: 'млн',
  separator: ',',
  thousandPostfix: 'тыс',
};

export enum VILLAGE_CLASS_KEYS {
  ECONOMY = 'economy',
  COMFORT = 'comfort',
  BUSINESS = 'business',
  ELITE = 'elite',
}

export const DEFAULT_GET_VILLAGE_LIST_REQUEST: IGetVillageListRequestSchema = {
  newobjectType: [
    NEW_OBJECTS_KEYS.COTTAGE,
    NEW_OBJECTS_KEYS.LANDPLOT,
    NEW_OBJECTS_KEYS.LOWRISEHOUSE,
    NEW_OBJECTS_KEYS.TOWNHOUSE,
  ] as unknown as ENewobjectType[],
  p: 1,
  pageSize: 25,
  villageClass: [
    VILLAGE_CLASS_KEYS.ECONOMY,
    VILLAGE_CLASS_KEYS.BUSINESS,
    VILLAGE_CLASS_KEYS.COMFORT,
    VILLAGE_CLASS_KEYS.ELITE,
  ] as unknown as EVillageClass[],
};

export const getDefaultVillageListRequest = (): IGetVillageListRequestSchema => ({
  ...DEFAULT_GET_VILLAGE_LIST_REQUEST,
  sorting: getSortingKeys().DEFAULT as ESorting,
});

export const VillageObjectTypes = {
  Cottage: ENewobjectType.Cottage,
  Landplot: ENewobjectType.Landplot,
  Lowrisehouse: ENewobjectType.Lowrisehouse,
  Townhouse: ENewobjectType.Townhouse,
};

export const VillageObjectNames = {
  [VillageObjectTypes.Cottage]: 'коттедж',
  [VillageObjectTypes.Landplot]: 'участок',
  [VillageObjectTypes.Lowrisehouse]: 'малоэтажный дом',
  [VillageObjectTypes.Townhouse]: 'таунхаус',
};

export enum Regions {
  Moscow = 1,
  MO = 4593,
  LO = 4588,
  Petersburg = 2,
}

export const MODERATION_STATUS_TYPES = {
  Deleted: EModerationStatus.Deleted,
  Draft: EModerationStatus.Draft,
  Published: EModerationStatus.Published,
};

export type TIndexableCounterSet = {
  [key: string]: keyof IVillageCounterSetSchema;
};

export const ON_SALE_OBJECTS: TIndexableCounterSet = {
  [NEW_OBJECTS_KEYS.COTTAGE]: 'onSaleCottage',
  [NEW_OBJECTS_KEYS.TOWNHOUSE]: 'onSaleTownhouse',
  [NEW_OBJECTS_KEYS.LANDPLOT]: 'onSaleLandplot',
  [NEW_OBJECTS_KEYS.LOWRISEHOUSE]: 'onSaleLowrisehouse',
};

export const DEFAULT_REGION_CITIES_MAP: { [key: number]: ICity[] } = {
  [Regions.Moscow]: [
    {
      displayName: 'Москва',
      fullName: 'Москва',
      id: 1,
    },
    {
      displayName: 'Московская область',
      fullName: 'Московская область',
      id: 4593,
    },
  ],
  [Regions.Petersburg]: [
    {
      displayName: 'Санкт-Петербург',
      fullName: 'Санкт-Петербург',
      id: 2,
    },
    {
      displayName: 'Ленинградская область',
      fullName: 'Ленинградская область',
      id: 4588,
    },
  ],
};

const MOSCOW_TEMPLATE = {
  displayName: MOSCOW_MO,
  fullName: MOSCOW_MO,
  id: 1,
};

const SPB_TEMPLATE = {
  displayName: SPB_LO,
  fullName: SPB_LO,
  id: 2,
};

export const DEFAULT_REGIONS_MAP: { [key: number]: ICity } = {
  [Regions.Moscow]: MOSCOW_TEMPLATE,
  4593: {
    ...MOSCOW_TEMPLATE,
    id: 4593,
    shadowId: 1,
  },
  [Regions.Petersburg]: SPB_TEMPLATE,
  4588: {
    ...SPB_TEMPLATE,
    id: 4588,
    shadowId: 2,
  },
};

export const MAX_SUGGESTIONS = 4;
export const DEFAULT_MAX_VILLAGE_SUGGESTIONS = 6;
export const MAX_VILLAGE_SUGGESTIONS = 8;

export const DEFAULT_MAX_BUILDER_SUGGESTIONS = 4;

export const COMMUTE_TYPES_MAP: TIndexableObject = {
  [Regions.Moscow]: 'МКАД',
  [Regions.MO]: 'МКАД',
  [Regions.Petersburg]: 'КАД',
  [Regions.LO]: 'КАД',
};

export const AREA_RANGE_VARIANTS: TVariant = ['сотки', 'соток', 'соток'];
export const AREA_VARIANTS: TVariant = ['сотка', 'сотки', 'соток'];
export const HOUSE_VARIANTS: TVariant = ['дом', 'дома', 'домов'];
export const TOWNHOUSE_VARIANTS: TVariant = ['таунхаус', 'таунхауcа', 'таунхауcов'];
export const COTTAGE_VARIANTS: TVariant = ['коттедж', 'коттеджа', 'коттеджей'];
export const LAND_VARIANTS: TVariant = ['участок', 'участка', 'участков'];
export const VILLAGE_VARIANTS: TVariant = ['коттеджный посёлок', 'коттеджных посёлка', 'коттеджных посёлков'];
